import React, { useCallback, useEffect, useRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import AntLocalization from '../../utils/AntLocalization';
import './styles.scss';

const SIZE_PRESETS = {
    sm: { width: 420, height: 'auto' },
    md: { width: 640, height: 'auto' },
    lg: { width: 820, height: 600 },
    xl: { width: 1100, height: 720 },
    full: { width: '100%', height: '100%' }
};

function resolveLocalized(v) {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number') return v;
    if (typeof v === 'object' && !React.isValidElement(v)) {
        if (v.vi || v.en) return AntLocalization.translateText(v);
    }
    return v;
}

function ErrorPanel({ error }) {
    if (!error) return null;
    let type = 'error';
    let message = error;
    if (typeof error === 'object' && !React.isValidElement(error)) {
        type = error.type || 'error';
        message = error.message || error.text || '';
    }
    if (!message) return null;
    return (
        <div className={'asp-shell__error asp-shell__error--' + type}>
            <span className="asp-shell__error-text">{resolveLocalized(message)}</span>
        </div>
    );
}

function ActionsBar({ actions, onClose }) {
    if (!actions || actions.length === 0) return null;
    // Wrap in .ant-bottom-bar so footer buttons inherit ante-react-kit standard
    // (uppercase 12px text, padding 7/18/8, min-height 32px) — same as AntPopup-based popups.
    return (
        <div className="asp-shell__actions ant-bottom-bar">
            {actions.map((a, i) => {
                if (!a || a.visible === false) return null;
                if (React.isValidElement(a)) return <React.Fragment key={i}>{a}</React.Fragment>;
                const handle = () => {
                    if (a.closeOnClick !== false && (a.variant === 'secondary' || a.role === 'cancel') && !a.onClick) {
                        onClose && onClose();
                        return;
                    }
                    a.onClick && a.onClick();
                };
                const btnClass = 'ant-processing-button' + (a.loading ? ' asp-btn--loading' : '');
                return (
                    <Button
                        key={i}
                        type={a.primary ? 'default' : (a.variant === 'danger' ? 'danger' : 'normal')}
                        stylingMode={a.primary ? 'contained' : (a.variant === 'secondary' ? 'text' : 'outlined')}
                        disabled={a.disabled || a.loading}
                        onClick={handle}
                        elementAttr={{ class: btnClass }}
                    >
                        {/* DevExtreme quản lý DOM bên trong Button. Giữ cây node ổn định
                            khi loading đổi trạng thái để React không removeChild một node
                            đã bị widget reparent/replace. */}
                        <LoadIndicator
                            className={'ant-indicator-small asp-action-indicator' + (a.loading ? '' : ' asp-action-indicator--hidden')}
                            visible={true}
                        />
                        {a.icon && (
                            <i className={'dx-icon dx-icon-' + a.icon + (a.loading ? ' asp-action-content--hidden' : '')} />
                        )}
                        <span className={'dx-button-text' + (a.loading ? ' asp-action-content--hidden' : '')}>
                            {resolveLocalized(a.label || a.text)}
                        </span>
                    </Button>
                );
            })}
        </div>
    );
}

export default function AntStandardPopup(props) {
    const {
        visible, onClose, onShown, onHidden,
        title, subtitle, headerLeft, headerRight, headerExtra,
        children, body,
        footer, actions,
        error,
        size = 'md', width, height, maxHeight, minHeight,
        hideOnOutsideClick = true, shading = true,
        autoFocusSelector = 'input:not([type=hidden]):not([disabled]),textarea:not([disabled])',
        confirmOnClose,
        wrapperClass,
        contentPadding = true,
        showCloseButton = true
    } = props;

    const popupRef = useRef(null);
    const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;
    const finalWidth = width != null ? width : preset.width;
    const finalHeight = height != null ? height : preset.height;

    const handleHiding = useCallback((e) => {
        if (confirmOnClose) {
            const ok = confirmOnClose();
            if (!ok) {
                if (e) e.cancel = true;
                return;
            }
        }
        onClose && onClose();
    }, [confirmOnClose, onClose]);

    const handleShown = useCallback((e) => {
        try {
            const el = e && e.component && e.component.content();
            if (el && autoFocusSelector) {
                const target = el.querySelector(autoFocusSelector);
                if (target) {
                    setTimeout(() => { try { target.focus(); target.select && target.select(); } catch (err) { /* ignore */ } }, 30);
                }
            }
        } catch (err) { /* ignore */ }
        onShown && onShown(e);
    }, [autoFocusSelector, onShown]);

    useEffect(() => {
        if (!visible || !confirmOnClose) return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (!confirmOnClose()) {
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [visible, confirmOnClose]);

    const titleText = resolveLocalized(title);
    const subtitleText = resolveLocalized(subtitle);
    const wrapClass = 'ant-standard-popup' + (wrapperClass ? ' ' + wrapperClass : '');

    return (
        <Popup
            ref={popupRef}
            visible={visible}
            onHiding={handleHiding}
            onShown={handleShown}
            onHidden={onHidden}
            dragEnabled={false}
            showCloseButton={false}
            hideOnOutsideClick={hideOnOutsideClick}
            shading={shading}
            showTitle={false}
            width={finalWidth}
            height={finalHeight}
            maxHeight={maxHeight}
            minHeight={minHeight}
            wrapperAttr={{ class: wrapClass }}
        >
            <div
                className={'asp-shell' + (size === 'full' ? ' asp-shell--full' : '')}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onScroll={(e) => e.stopPropagation()}
            >
                {(titleText || subtitleText || headerLeft || headerRight || headerExtra || showCloseButton) && (
                    <div className="asp-shell__header">
                        <div className="asp-shell__header-row">
                            {headerLeft && <div className="asp-shell__header-left">{headerLeft}</div>}
                            <div className="asp-shell__header-title-wrap">
                                {titleText && <div className="asp-shell__title">{titleText}</div>}
                                {subtitleText && <div className="asp-shell__subtitle">{subtitleText}</div>}
                            </div>
                            {headerRight && <div className="asp-shell__header-right">{headerRight}</div>}
                            {showCloseButton && (
                                <Button icon="close" stylingMode="text" onClick={handleHiding} elementAttr={{ class: 'asp-shell__close-btn' }} />
                            )}
                        </div>
                        {headerExtra && <div className="asp-shell__header-extra">{headerExtra}</div>}
                    </div>
                )}

                <div className={'asp-shell__body' + (contentPadding ? '' : ' asp-shell__body--flush')}>
                    {body || children}
                </div>

                <ErrorPanel error={error} />

                {footer ? (
                    <div className="asp-shell__footer">{footer}</div>
                ) : (
                    actions && actions.length > 0 && (
                        <div className="asp-shell__footer">
                            <ActionsBar actions={actions} onClose={() => handleHiding()} />
                        </div>
                    )
                )}
            </div>
        </Popup>
    );
}

AntStandardPopup.SIZES = Object.keys(SIZE_PRESETS);
